import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  IconButton,
  Collapse,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PlayCircle as PlayIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
  ArrowBack as ArrowBackIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@store/authStore';
import { getPortalPaths, type PortalMode } from '@utils/portalPaths';
import { toast } from 'react-toastify';

interface Skill {
  id: string;
  skillName: string;
  rankingStrokeCode?: string;
  categoryId?: string;
  subcategoryId?: string;
  description?: string;
  explanation?: string;
  practiceSteps?: string[];
  tips?: string[];
  referenceVideos: string[];
  difficultyLevel?: number;
  tags?: string[];
  isAdvancedLevel?: boolean;
  isProfessionalLevel?: boolean;
  pathPlayer: boolean;
  pathCoach: boolean;
  displayOrder: number;
  // Additional fields for different content types
  title?: string;
  // New schema: JSONB fields
  measurements?: any; // JSONB array of dimension objects: [{Dimensions: "name", Value: {key: value}}]
  images?: any; // JSONB array: [{title, url, Author}]
  videos?: any; // JSONB array: [{title, url, Author}]
  // Old schema fields (for backward compatibility - will be removed)
  courtType?: string;
  lengthMeters?: number;
  widthSinglesMeters?: number;
  widthDoublesMeters?: number;
  diagramImageUrl?: string;
  contentType?: string; // 'skill', 'court_dimensions', 'equipment', etc.
}

interface Category {
  id: string;
  name: string;
  parentCategoryId?: string;
  description?: string;
  displayOrder: number;
  subcategories?: Category[];
  skillCount?: number;
  tableName?: string;
}

const DRAWER_WIDTH = 250;

interface SportLearningPageProps {
  portalMode?: PortalMode;
}

const SportLearningPage: React.FC<SportLearningPageProps> = ({ portalMode = 'player' }) => {
  const { sport } = useParams<{ sport: string }>();
  const navigate = useNavigate();
  const paths = getPortalPaths(portalMode);
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedPath, setSelectedPath] = useState<'Player' | 'Coach'>('Player');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [videoIndices, setVideoIndices] = useState<Record<string, number>>({});
  const [progress, setProgress] = useState<Record<string, { contentRead: boolean; videoWatched: boolean; practiceCompleted: boolean; completedAt?: string }>>({});
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
  const [categorySkillCounts, setCategorySkillCounts] = useState<Record<string, number>>({});

  const sportName = sport ? sport.charAt(0).toUpperCase() + sport.slice(1).toLowerCase() : 'Badminton';

  useEffect(() => {
    loadPlayerId();
  }, []);

  useEffect(() => {
    if (sport) {
      console.log('[SportLearningPage] Loading categories for sport:', sport);
      loadCategories();
    }
  }, [sport]);

  useEffect(() => {
    if (sport) {
      console.log('[SportLearningPage] Path or category changed, loading skills');
      loadSkills();
    }
  }, [sport, selectedPath, selectedCategoryId]);

  useEffect(() => {
    if (playerId && sport) {
      loadProgress();
    }
  }, [playerId, sport, selectedPath]);

  const loadPlayerId = async () => {
    try {
      const response = await fetch(`/api/players/me?userId=${user?.id}`);
      const result = await response.json();
      if (result.success && result.data) {
        setPlayerId(result.data.id);
      }
    } catch (err) {
      console.error('Error loading player ID:', err);
    }
  };

  const loadCategories = async () => {
    if (!sport) return;
    
    // Capitalize first letter for API call (e.g., "badminton" -> "Badminton")
    const sportCapitalized = sport.charAt(0).toUpperCase() + sport.slice(1).toLowerCase();
    
    console.log('[SportLearningPage] Loading categories for sport:', sport, '->', sportCapitalized);
    try {
      const response = await fetch(`/api/learning/categories?sport=${sportCapitalized}`);
      const result = await response.json();
      
      console.log('[SportLearningPage] Categories API response:', result);
      
      if (result.success && result.data && Array.isArray(result.data)) {
        // Separate main categories and subcategories (API returns snake_case)
        const allCategories = result.data;
        const mainCategories = allCategories.filter((cat: any) => !cat.parent_category_id);
        const subcategories = allCategories.filter((cat: any) => cat.parent_category_id);
        
        // Group subcategories under their parent categories
        const categoriesWithSubs = mainCategories.map((mainCat: any) => {
          const subs = subcategories
            .filter((sub: any) => sub.parent_category_id === mainCat.id)
            .sort((a: any, b: any) => (a.display_order || 0) - (b.display_order || 0));
          return {
            id: mainCat.id,
            name: mainCat.name,
            description: mainCat.description,
            displayOrder: mainCat.display_order || 0,
            tableName: mainCat.table_name || mainCat.full_table_name,
            subcategories: subs.length > 0 ? subs.map((sub: any) => ({
              id: sub.id,
              name: sub.name,
              description: sub.description,
              displayOrder: sub.display_order || 0,
              tableName: sub.table_name || sub.full_table_name,
            })) : undefined
          };
        }).sort((a: Category, b: Category) => a.displayOrder - b.displayOrder);
        
        // Initialize expanded state for categories with subcategories
        const initialExpanded: Record<string, boolean> = {};
        categoriesWithSubs.forEach(cat => {
          if (cat.subcategories && cat.subcategories.length > 0) {
            initialExpanded[cat.id] = false; // Start collapsed
          }
        });
        setExpandedCategories(initialExpanded);
        
        console.log('[SportLearningPage] Loaded', categoriesWithSubs.length, 'main categories');
        console.log('[SportLearningPage] Subcategories:', subcategories.length);
        setCategories(categoriesWithSubs);
        
        // Load skill counts for all categories (including parent categories)
        // For parent categories, we'll aggregate from subcategories when they're loaded
        const initialCounts: Record<string, number> = {};
        categoriesWithSubs.forEach(cat => {
          // Initialize parent category count to 0 (will be updated when subcategories load)
          if (cat.subcategories && cat.subcategories.length > 0) {
            initialCounts[cat.id] = 0;
            // Initialize subcategory counts to 0
            cat.subcategories.forEach(sub => {
              initialCounts[sub.id] = 0;
            });
          }
        });
        setCategorySkillCounts(initialCounts);
      } else {
        console.error('[SportLearningPage] Failed to load categories:', result.message);
        setError('Failed to load categories');
        setCategories([]);
      }
    } catch (err: any) {
      console.error('[SportLearningPage] Error loading categories:', err);
      setError('Failed to load categories: ' + (err.message || 'Unknown error'));
      setCategories([]);
    }
  };

  const loadSkills = async () => {
    if (!sport) {
      setSkills([]);
      return;
    }
    
    // If "all" is selected, don't load (or load from all categories)
    if (selectedCategoryId === 'all') {
      setSkills([]);
      return;
    }
    
    // Find the selected category to get its table_name
    const selectedCategory = categories.find(c => c.id === selectedCategoryId) ||
      categories.flatMap(c => c.subcategories || []).find(s => s.id === selectedCategoryId);
    
    if (!selectedCategoryId || !selectedCategory) {
      setSkills([]);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      // Use generic admin API endpoint (works for players too, just needs categoryId)
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      const url = `/api/admin/learning/skills?categoryId=${selectedCategoryId}`;
      console.log('[SportLearningPage] Fetching skills from:', url);
      
      const response = await fetch(url, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      const result = await response.json();
      
      console.log('[SportLearningPage] API response - success:', result.success, 'data length:', result.data?.length);
      
      if (result.success) {
        const skillsData = (result.data || []).map((item: any) => ({
          id: item.id,
          skillName: item.skill_name || item.title || item.equipment_name || 'Untitled',
          rankingStrokeCode: item.ranking_stroke_code,
          categoryId: item.category_id,
          subcategoryId: item.subcategory_id,
          description: item.description || '',
          explanation: item.explanation || item.detailed_explanation || '',
          practiceSteps: item.practice_steps || [],
          tips: item.tips || [],
          referenceVideos: item.reference_videos && Array.isArray(item.reference_videos) 
            ? item.reference_videos 
            : [],
          difficultyLevel: item.difficulty_level,
          tags: item.tags || [],
          isAdvancedLevel: item.is_advanced_level || false,
          isProfessionalLevel: item.is_professional_level || false,
          pathPlayer: item.path_player !== false,
          pathCoach: item.path_coach !== false,
          displayOrder: item.display_order || 0,
          // Additional fields for Court Dimensions and other content types
          title: item.title,
          // New schema: measurements, images, videos (JSONB) - parse if string
          measurements: (() => {
            if (!item.measurements) return null;
            if (typeof item.measurements === 'string') {
              try { return JSON.parse(item.measurements); } catch { return null; }
            }
            return item.measurements;
          })(),
          images: (() => {
            if (!item.images) return null;
            if (typeof item.images === 'string') {
              try { return JSON.parse(item.images); } catch { return null; }
            }
            return item.images;
          })(),
          videos: (() => {
            if (!item.videos) return null;
            if (typeof item.videos === 'string') {
              try { return JSON.parse(item.videos); } catch { return null; }
            }
            return item.videos;
          })(),
          text_content: (() => {
            if (!item.text_content) return null;
            if (typeof item.text_content === 'string') {
              try { return JSON.parse(item.text_content); } catch { return null; }
            }
            return item.text_content;
          })(),
          // Old schema fields (for backward compatibility - will be removed)
          // Only set if they have actual values (not null/undefined/empty)
          courtType: item.court_type || undefined,
          lengthMeters: item.length_meters || undefined,
          widthSinglesMeters: item.width_singles_meters || undefined,
          widthDoublesMeters: item.width_doubles_meters || undefined,
          diagramImageUrl: item.diagram_image_url || undefined,
          contentType: item.title ? 'court_dimensions' : (item.equipment_name ? 'equipment' : 'skill'),
          categoryType: item.category_type || null, // Add category_type from backend
        }));
        console.log('[SportLearningPage] Loaded', skillsData.length, 'items');
        setSkills(skillsData);
        
        // Update skill count for this category
        setCategorySkillCounts(prev => ({
          ...prev,
          [selectedCategoryId]: skillsData.length
        }));
        
        // If this is a subcategory, also update parent category count
        setCategorySkillCounts(prev => {
          const updated = { ...prev, [selectedCategoryId]: skillsData.length };
          
          // Find parent category for this subcategory
          const parentCategory = categories.find(c => 
            c.subcategories && c.subcategories.some(sub => sub.id === selectedCategoryId)
          );
          
          if (parentCategory && parentCategory.subcategories) {
            // Aggregate counts from all subcategories of this parent
            const allSubcategoryIds = parentCategory.subcategories.map(sub => sub.id);
            const totalCount = allSubcategoryIds.reduce((sum, subId) => {
              // Use updated count for current subcategory, prev for others
              const count = subId === selectedCategoryId ? skillsData.length : (prev[subId] || 0);
              return sum + count;
            }, 0);
            
            updated[parentCategory.id] = totalCount;
          }
          
          return updated;
        });
        
        // Initialize video indices
        const indices: Record<string, number> = {};
        skillsData.forEach((skill: Skill) => {
          if (skill.referenceVideos && skill.referenceVideos.length > 0) {
            indices[skill.id] = 0;
          }
        });
        setVideoIndices(indices);
      } else {
        console.error('[SportLearningPage] API error:', result.message);
        setError(result.message || 'Failed to load content');
        setSkills([]);
        setCategorySkillCounts(prev => ({
          ...prev,
          [selectedCategoryId]: 0
        }));
      }
    } catch (err: any) {
      console.error('[SportLearningPage] Error loading skills:', err);
      setError('Failed to load content: ' + (err.message || 'Unknown error'));
      setSkills([]);
      setCategorySkillCounts(prev => ({
        ...prev,
        [selectedCategoryId]: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async () => {
    if (!playerId || !sport) return;
    
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
      const response = await fetch(`/api/learning/progress?path=${selectedPath}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      const result = await response.json();
      if (result.success) {
        const progressMap: Record<string, { contentRead: boolean; videoWatched: boolean; practiceCompleted: boolean; completedAt?: string }> = {};
        result.data.forEach((item: any) => {
          if (item.skillTable === 'learning_skills_strokes_shots') {
            progressMap[item.skillId] = {
              contentRead: item.contentRead || false,
              videoWatched: item.videoWatched || false,
              practiceCompleted: item.practiceCompleted || false,
              completedAt: item.completedAt
            };
          }
        });
        setProgress(progressMap);
      }
    } catch (err) {
      console.error('Error loading progress:', err);
    }
  };

  const handleToggleExpand = (skillId: string) => {
    setExpandedSkills(prev => ({
      ...prev,
      [skillId]: !prev[skillId]
    }));
  };

  const handleVideoNavigation = (skillId: string, direction: 'prev' | 'next') => {
    const skill = skills.find(s => s.id === skillId);
    if (!skill || !skill.referenceVideos || skill.referenceVideos.length <= 1) return;

    setVideoIndices(prev => {
      const currentIndex = prev[skillId] || 0;
      const maxIndex = skill.referenceVideos.length - 1;
      let newIndex = currentIndex;
      
      if (direction === 'prev') {
        newIndex = currentIndex > 0 ? currentIndex - 1 : maxIndex;
      } else {
        newIndex = currentIndex < maxIndex ? currentIndex + 1 : 0;
      }
      
      return { ...prev, [skillId]: newIndex };
    });
  };

  const handleMarkComplete = async (skillId: string, contentRead: boolean, videoWatched: boolean, practiceCompleted: boolean) => {
    if (!playerId) return;
    
    setSaving(prev => ({ ...prev, [skillId]: true }));
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
      const response = await fetch('/api/learning/progress/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          skillId,
          skillTable: 'learning_skills_strokes_shots',
          contentRead,
          videoWatched,
          practiceCompleted,
        })
      });
      const result = await response.json();
      
      if (result.success) {
        toast.success('Progress saved successfully');
        if (contentRead && videoWatched && practiceCompleted) {
          toast.success('Skill marked as complete!');
        }
        loadProgress();
      } else {
        throw new Error(result.message || 'Failed to save progress');
      }
    } catch (err: any) {
      console.error('Error saving progress:', err);
      toast.error(err.message || 'Failed to save progress');
    } finally {
      setSaving(prev => ({ ...prev, [skillId]: false }));
    }
  };

  const handleProgressChange = (skillId: string, type: 'contentRead' | 'videoWatched' | 'practiceCompleted', checked: boolean) => {
    const currentProgress = progress[skillId] || { contentRead: false, videoWatched: false, practiceCompleted: false };
    const newProgress = {
      ...currentProgress,
      [type]: checked
    };
    
    setProgress(prev => ({
      ...prev,
      [skillId]: newProgress
    }));

    // Auto-save
    handleMarkComplete(skillId, newProgress.contentRead, newProgress.videoWatched, newProgress.practiceCompleted);
  };

  const sidebar = (
    <Box sx={{ width: DRAWER_WIDTH, p: 2, height: '100vh', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate(paths.learningCourses)} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {sportName}
        </Typography>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      <List>
        <ListItem disablePadding>
          <ListItemButton
            selected={selectedCategoryId === 'all'}
            onClick={() => setSelectedCategoryId('all')}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              '&.Mui-selected': {
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                }
              }
            }}
          >
            <ListItemText 
              primary="All Categories"
              primaryTypographyProps={{ fontWeight: selectedCategoryId === 'all' ? 600 : 400 }}
            />
          </ListItemButton>
        </ListItem>
        {categories.map((cat, index) => (
          <React.Fragment key={cat.id}>
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedCategoryId === cat.id}
                onClick={() => setSelectedCategoryId(cat.id)}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  pl: cat.subcategories && cat.subcategories.length > 0 ? 1 : 2, // Align items without subcategories
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {cat.subcategories && cat.subcategories.length > 0 ? (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedCategories(prev => ({
                          ...prev,
                          [cat.id]: !prev[cat.id]
                        }));
                      }}
                      sx={{ mr: 1, color: 'inherit', minWidth: 32 }}
                    >
                      {expandedCategories[cat.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  ) : (
                    <Box sx={{ width: 32, mr: 1 }} /> // Spacer to align with items that have arrows
                  )}
                  <ListItemText 
                    primary={`${index + 1}. ${cat.name}`}
                    secondary={cat.description || (expandedCategories[cat.id] ? `${categorySkillCounts[cat.id] ?? 0} items` : '')}
                    primaryTypographyProps={{ fontWeight: selectedCategoryId === cat.id ? 600 : 400 }}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </ListItemButton>
            </ListItem>
            {cat.subcategories && cat.subcategories.length > 0 && (
              <Collapse in={expandedCategories[cat.id] || false} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {cat.subcategories.map((sub, subIndex) => (
                    <ListItem key={sub.id} disablePadding sx={{ pl: 4 }}>
                      <ListItemButton
                        selected={selectedCategoryId === sub.id}
                        onClick={() => setSelectedCategoryId(sub.id)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          fontSize: '0.875rem',
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                          }
                        }}
                      >
                        <ListItemText 
                          primary={`${index + 1}.${subIndex + 1} ${sub.name}`}
                          secondary={expandedCategories[cat.id] ? `${categorySkillCounts[sub.id] ?? 0} items` : ''}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebar}
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Paper elevation={2} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 700 }}>
                {sportName} Learning Module
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Master {sportName} skills through structured learning paths. {skills.length > 0 && `Explore ${skills.length} skills available.`}
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: 150, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1 }}>
              <InputLabel sx={{ color: 'white' }}>Learning Path</InputLabel>
              <Select
                value={selectedPath}
                label="Learning Path"
                onChange={(e) => setSelectedPath(e.target.value as 'Player' | 'Coach')}
                sx={{ 
                  color: 'white',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255,255,255,0.8)',
                  },
                  '& .MuiSvgIcon-root': {
                    color: 'white',
                  }
                }}
              >
                <MenuItem value="Player">Player</MenuItem>
                <MenuItem value="Coach">Coach</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>Loading skills...</Typography>
          </Box>
        ) : skills.length > 0 ? (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {(() => {
                    if (selectedCategoryId === 'all') return 'All Categories';
                    const selectedCat = categories.find(c => c.id === selectedCategoryId) ||
                      categories.flatMap(c => c.subcategories || []).find(s => s.id === selectedCategoryId);
                    if (!selectedCat) return 'Content';
                    // Determine content type based on category name or table name
                    const catName = selectedCat.name.toLowerCase();
                    if (catName.includes('court') || catName.includes('dimension')) return selectedCat.name;
                    if (catName.includes('equipment') || catName.includes('racket')) return selectedCat.name;
                    return `${selectedCat.name} Skills`;
                  })()}
                </Typography>
              </Box>
            </Box>
            <Grid container spacing={3}>
              {skills.map((skill) => {
                // Check if this is General category type (applies to all games, not just Badminton)
                // Use category_type if available, otherwise fall back to contentType/title check
                const isGeneralType = skill.categoryType === 'general' || 
                                     skill.category_type === 'general' ||
                                     (skill.contentType === 'court_dimensions' || !!skill.title);
                
                if (isGeneralType) {
                  // Render Court Dimensions as a detailed card
                  return (
                    <Grid item xs={12} key={skill.id}>
                      <Card sx={{ mb: 3 }}>
                        <CardContent>
                          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
                            {skill.title || skill.skillName}
                          </Typography>
                          
                          {/* Display content in form order: Title, Description, Explanation, Images, Videos, Notes */}
                          
                          {/* Description - shown first after title */}
                          {skill.description && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                Description
                              </Typography>
                              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                {skill.description}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Explanation - shown after description */}
                          {skill.explanation && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                                Explanation
                              </Typography>
                              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                {skill.explanation}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Images - shown after description and explanation */}
                          {skill.images && Array.isArray(skill.images) && skill.images.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                Images
                              </Typography>
                              {skill.images.map((img: any, imgIndex: number) => (
                                <Box key={imgIndex} sx={{ mb: 3 }}>
                                  {/* Text Before Image */}
                                  {img.textBefore && (
                                    <Box sx={{ mb: 2 }}>
                                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                        {img.textBefore}
                                      </Typography>
                                    </Box>
                                  )}
                                  
                                  {/* Image */}
                                  <Box sx={{ mb: 2, textAlign: 'center' }}>
                                    {img.url && (
                                      <img 
                                        src={img.url} 
                                        alt={img.title || `Image ${imgIndex + 1}`} 
                                        style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
                                      />
                                    )}
                                    {img.title && (
                                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        {img.title}
                                      </Typography>
                                    )}
                                    {img.Author && (
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Source: {img.Author}
                                      </Typography>
                                    )}
                                  </Box>
                                  
                                  {/* Text After Image */}
                                  {img.textAfter && (
                                    <Box sx={{ mt: 2 }}>
                                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                        {img.textAfter}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                          
                          {/* Videos - shown after images */}
                          {skill.videos && Array.isArray(skill.videos) && skill.videos.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                Videos
                              </Typography>
                              {skill.videos.map((video: any, vidIndex: number) => {
                                if (!video.url) return null;
                                
                                // Extract YouTube video ID
                                const getYouTubeVideoId = (url: string): string | null => {
                                  const patterns = [
                                    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
                                    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
                                  ];
                                  for (const pattern of patterns) {
                                    const match = url.match(pattern);
                                    if (match && match[1]) return match[1];
                                  }
                                  return null;
                                };
                                
                                const videoId = getYouTubeVideoId(video.url);
                                const isYouTube = !!videoId;
                                
                                return (
                                  <Box key={vidIndex} sx={{ mb: 3 }}>
                                    {/* Text Before Video */}
                                    {video.textBefore && (
                                      <Box sx={{ mb: 2 }}>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                          {video.textBefore}
                                        </Typography>
                                      </Box>
                                    )}
                                    
                                    {video.title && (
                                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                        {video.title}
                                      </Typography>
                                    )}
                                    {isYouTube && videoId ? (
                                      <Box
                                        sx={{
                                          position: 'relative',
                                          paddingBottom: '56.25%', // 16:9 aspect ratio
                                          height: 0,
                                          overflow: 'hidden',
                                          borderRadius: 1,
                                          bgcolor: '#000',
                                        }}
                                      >
                                        <iframe
                                          src={`https://www.youtube.com/embed/${videoId}`}
                                          style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            border: 0,
                                          }}
                                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                          allowFullScreen
                                          title={video.title || `Video ${vidIndex + 1}`}
                                        />
                                      </Box>
                                    ) : (
                                      <Box
                                        component="video"
                                        controls
                                        sx={{
                                          width: '100%',
                                          maxHeight: '500px',
                                          borderRadius: 1,
                                        }}
                                      >
                                        <source src={video.url} type="video/mp4" />
                                        Your browser does not support the video tag.
                                      </Box>
                                    )}
                                    {video.Author && (
                                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        Source: {video.Author}
                                      </Typography>
                                    )}
                                    
                                    {/* Text After Video */}
                                    {video.textAfter && (
                                      <Box sx={{ mt: 2 }}>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                                          {video.textAfter}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                          
                          {/* New Schema: Display text content from JSONB */}
                          {skill.text_content && Array.isArray(skill.text_content) && skill.text_content.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                Text Content
                              </Typography>
                              {skill.text_content.map((text: any, textIndex: number) => (
                                <Box key={textIndex} sx={{ mb: 3, p: 2, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                                  {text.title && (
                                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                      {text.title}
                                    </Typography>
                                  )}
                                  {text.content && (
                                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mb: 1 }}>
                                      {text.content}
                                    </Typography>
                                  )}
                                  {text.Author && (
                                    <Typography variant="caption" color="text.secondary">
                                      Source: {text.Author}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                          
                          {/* Old Schema: Backward compatibility */}
                          {!skill.images && skill.diagramImageUrl && (
                            <Box sx={{ mb: 3, textAlign: 'center' }}>
                              <img 
                                src={skill.diagramImageUrl} 
                                alt={skill.title || 'Court Diagram'}
                                style={{ maxWidth: '100%', height: 'auto', borderRadius: 8 }}
                              />
                            </Box>
                          )}
                          
                          {/* Notes - shown last */}
                          {/* New Schema: Display measurements from JSONB */}
                          {skill.measurements && Array.isArray(skill.measurements) && skill.measurements.length > 0 && (
                            <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                Notes
                              </Typography>
                              {skill.measurements.map((dimension: any, dimIndex: number) => {
                                // Handle both formats: {Dimensions, Value} or {key, value}
                                const dimensionName = dimension.Dimensions || dimension.dimensions || dimension.name || '';
                                const dimensionValue = dimension.Value || dimension.value || {};
                                
                                // If it's a simple {key, value} format, display directly
                                if (dimension.key && dimension.value) {
                                  return (
                                    <Box key={dimIndex} sx={{ mb: 1, p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                          <Typography variant="body2" color="text.secondary">{dimension.key}</Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {String(dimension.value)}
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    </Box>
                                  );
                                }
                                
                                // Handle {Dimensions, Value} format
                                return (
                                  <Box key={dimIndex} sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                                    {dimensionName && (
                                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
                                        {dimensionName}
                                      </Typography>
                                    )}
                                    {dimensionValue && typeof dimensionValue === 'object' && Object.keys(dimensionValue).length > 0 && (
                                      <Grid container spacing={2}>
                                        {Object.entries(dimensionValue).map(([key, value]) => (
                                          <Grid item xs={12} sm={6} key={key}>
                                            <Typography variant="body2" color="text.secondary">{key}</Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                              {String(value)}
                                            </Typography>
                                          </Grid>
                                        ))}
                                      </Grid>
                                    )}
                                  </Box>
                                );
                              })}
                            </Box>
                          )}
                          
                          {/* Old Schema: Backward compatibility with warning - Only show if NO measurements exist */}
                          {(!skill.measurements || (Array.isArray(skill.measurements) && skill.measurements.length === 0)) 
                            && skill.courtType && skill.lengthMeters && (skill.widthSinglesMeters || skill.widthDoublesMeters) && (
                            <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
                              <Typography variant="body2" color="warning.main" sx={{ mb: 1, fontWeight: 600 }}>
                                ⚠️ Old data format - Please update this content to use the new structure
                              </Typography>
                              <Grid container spacing={2}>
                                {skill.courtType && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Court Type</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {skill.courtType.charAt(0).toUpperCase() + skill.courtType.slice(1)}
                                    </Typography>
                                  </Grid>
                                )}
                                {skill.lengthMeters && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Length</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {skill.lengthMeters}m
                                    </Typography>
                                  </Grid>
                                )}
                                {skill.widthSinglesMeters && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Singles Width</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {skill.widthSinglesMeters}m
                                    </Typography>
                                  </Grid>
                                )}
                                {skill.widthDoublesMeters && (
                                  <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="text.secondary">Doubles Width</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                      {skill.widthDoublesMeters}m
                                    </Typography>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                }
                
                // Render regular skills as cards
                const skillProgress = progress[skill.id];
                const isComplete = skillProgress?.contentRead && skillProgress?.videoWatched && skillProgress?.practiceCompleted;
                const hasVideo = skill.referenceVideos && skill.referenceVideos.length > 0;
                const hasContent = !!(skill.explanation || skill.practiceSteps?.length > 0);

                return (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2} key={skill.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: isComplete ? '2px solid #4caf50' : '1px solid rgba(0,0,0,0.12)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: 6,
                          transform: 'translateY(-4px)',
                        },
                        position: 'relative',
                      }}
                    >
                      {isComplete && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1,
                          }}
                        >
                          <CheckIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                        </Box>
                      )}
                      <CardActionArea
                        onClick={() => {
                          setSelectedSkill(skill);
                          setSkillDialogOpen(true);
                        }}
                        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                      >
                        <CardContent sx={{ flex: 1, p: 2 }}>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600, 
                              mb: 1,
                              fontSize: '1rem',
                              lineHeight: 1.3,
                              minHeight: '2.6em',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {skill.skillName}
                          </Typography>
                          
                          {skill.rankingStrokeCode && (
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              {skill.rankingStrokeCode}
                            </Typography>
                          )}

                          {skill.description && (
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                mb: 1.5,
                                fontSize: '0.75rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {skill.description}
                            </Typography>
                          )}

                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                            {skill.difficultyLevel && (
                              <Chip
                                label={`Lv ${skill.difficultyLevel}`}
                                size="small"
                                variant="outlined"
                                color={skill.difficultyLevel >= 4 ? 'error' : skill.difficultyLevel >= 3 ? 'warning' : 'default'}
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                            {skill.isAdvancedLevel && (
                              <Chip
                                label="Advanced"
                                size="small"
                                color="warning"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                            {skill.isProfessionalLevel && (
                              <Chip
                                label="Pro"
                                size="small"
                                color="error"
                                sx={{ fontSize: '0.7rem', height: 20 }}
                              />
                            )}
                          </Box>

                          <Divider sx={{ my: 1 }} />

                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', fontSize: '0.75rem' }}>
                            {hasVideo && (
                              <Chip
                                icon={<PlayIcon sx={{ fontSize: 14 }} />}
                                label={`${skill.referenceVideos.length}`}
                                size="small"
                                color={skillProgress?.videoWatched ? 'success' : 'default'}
                                sx={{ fontSize: '0.7rem', height: 22 }}
                              />
                            )}
                            {hasContent && (
                              <Chip
                                label="Content"
                                size="small"
                                color={skillProgress?.contentRead ? 'success' : 'default'}
                                sx={{ fontSize: '0.7rem', height: 22 }}
                              />
                            )}
                          </Box>

                          {skillProgress && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                              <Box sx={{ display: 'flex', gap: 1, fontSize: '0.7rem' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {skillProgress.contentRead ? (
                                    <CheckIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                  ) : (
                                    <UncheckedIcon sx={{ fontSize: 14 }} />
                                  )}
                                  <Typography variant="caption">Read</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {skillProgress.videoWatched ? (
                                    <CheckIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                  ) : (
                                    <UncheckedIcon sx={{ fontSize: 14 }} />
                                  )}
                                  <Typography variant="caption">Video</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  {skillProgress.practiceCompleted ? (
                                    <CheckIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                  ) : (
                                    <UncheckedIcon sx={{ fontSize: 14 }} />
                                  )}
                                  <Typography variant="caption">Practice</Typography>
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ) : null}

        {/* Skill Detail Dialog */}
        <Dialog
          open={skillDialogOpen}
          onClose={() => setSkillDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          {selectedSkill && (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {selectedSkill.skillName}
                  </Typography>
                  <IconButton onClick={() => setSkillDialogOpen(false)}>
                    <ArrowBackIcon />
                  </IconButton>
                </Box>
                {selectedSkill.rankingStrokeCode && (
                  <Typography variant="caption" color="text.secondary">
                    Code: {selectedSkill.rankingStrokeCode}
                  </Typography>
                )}
              </DialogTitle>
              <DialogContent dividers>
                {/* Videos Section */}
                {selectedSkill.referenceVideos && selectedSkill.referenceVideos.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ flex: 1 }}>
                        Reference Video{selectedSkill.referenceVideos.length > 1 ? `s (${(videoIndices[selectedSkill.id] || 0) + 1}/${selectedSkill.referenceVideos.length})` : ''}
                      </Typography>
                      {selectedSkill.referenceVideos.length > 1 && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleVideoNavigation(selectedSkill.id, 'prev')}
                          >
                            <NavigateBeforeIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleVideoNavigation(selectedSkill.id, 'next')}
                          >
                            <NavigateNextIcon />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                    <Box
                      sx={{
                        position: 'relative',
                        paddingBottom: '56.25%',
                        height: 0,
                        overflow: 'hidden',
                        borderRadius: 1,
                        bgcolor: '#000',
                        mb: 2,
                      }}
                    >
                      <iframe
                        src={selectedSkill.referenceVideos[videoIndices[selectedSkill.id] || 0]}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 0,
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title={`${selectedSkill.skillName} - Video ${(videoIndices[selectedSkill.id] || 0) + 1}`}
                      />
                    </Box>
                  </Box>
                )}

                {/* Description */}
                {selectedSkill.description && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedSkill.description}
                    </Typography>
                  </Box>
                )}

                {/* Explanation Section */}
                {selectedSkill.explanation && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Explanation
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                      {selectedSkill.explanation}
                    </Typography>
                  </Box>
                )}

                {/* Practice Steps Section */}
                {selectedSkill.practiceSteps && selectedSkill.practiceSteps.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Practice Steps
                    </Typography>
                    <List>
                      {selectedSkill.practiceSteps.map((step, idx) => (
                        <ListItem key={idx}>
                          <ListItemText
                            primary={`Step ${idx + 1}`}
                            secondary={step}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Tips Section */}
                {selectedSkill.tips && selectedSkill.tips.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Tips
                    </Typography>
                    <List>
                      {selectedSkill.tips.map((tip, idx) => (
                        <ListItem key={idx}>
                          <ListItemText secondary={tip} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Progress Checkboxes */}
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Mark Progress
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedSkill.explanation && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={progress[selectedSkill.id]?.contentRead || false}
                            onChange={(e) => handleProgressChange(selectedSkill.id, 'contentRead', e.target.checked)}
                            disabled={saving[selectedSkill.id]}
                          />
                        }
                        label="I have read the explanation"
                      />
                    )}
                    {selectedSkill.referenceVideos && selectedSkill.referenceVideos.length > 0 && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={progress[selectedSkill.id]?.videoWatched || false}
                            onChange={(e) => handleProgressChange(selectedSkill.id, 'videoWatched', e.target.checked)}
                            disabled={saving[selectedSkill.id]}
                          />
                        }
                        label="I have watched the video(s)"
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={progress[selectedSkill.id]?.practiceCompleted || false}
                          onChange={(e) => handleProgressChange(selectedSkill.id, 'practiceCompleted', e.target.checked)}
                          disabled={saving[selectedSkill.id]}
                        />
                      }
                      label="I have completed practice"
                    />
                  </Box>
                  {progress[selectedSkill.id]?.contentRead && progress[selectedSkill.id]?.videoWatched && progress[selectedSkill.id]?.practiceCompleted && (
                    <Chip
                      icon={<CheckIcon />}
                      label="Skill Completed"
                      color="success"
                      sx={{ mt: 2 }}
                    />
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSkillDialogOpen(false)}>Close</Button>
              </DialogActions>
            </>
          )}
        </Dialog>
      </Box>
    </Box>
  );
};

export default SportLearningPage;

